import React, { useMemo } from 'react'
import _ from 'lodash'
import styled from '@emotion/styled'
import Icon from 'shared/components/Icon'
import { LumenIcon } from 'shared/components/vega/icons'
import { ServiceCard, ServiceHeader } from 'shared/components/vega/Service'
import { Timestamp } from 'shared/components/vega/Timestamp'
import { ExplanationTooltip } from 'shared/components/vega/ExpressionEngine'
import { buildClassName } from 'shared/utils/vega'
import { getColorForLumenRun, getCheckStatusFromOutcomes, displayName } from './utils'
import { useVeroFlag } from 'shared/utils/featureFlags'
import { VeroFlag } from 'shared/utils/veroFlags'
import { bannerDate } from 'shared/utils/bannerDate'

const CosignActionsContainer = styled.div({
  display: 'inline-flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '16px',
})

type CosignButtonProps = {
  status?: 'available' | 'pending' | 'approved' | 'denied' | 'canceled' | 'expired';
}

const getCosignButtonStyles = (status?: string) => {
  if (status === 'approved') {
    return {
      border: '#1b7a6a',
      borderHover: '#1b7a6a',
      color: '#24e294',
      iconColor: '#10B981',
      backgroundColor: '#244854',
    };
  }
  if (status === 'pending') {
    return {
      border: '#D97706',
      borderHover: '#F59E0B',
      color: '#D97706',
      iconColor: '#3B82F6',
      backgroundColor: '#57413b',
    };
  }
  if (status === 'denied') {
    return {
      border: '#7f1d1d',
      borderHover: '#991b1b',
      color: '#f87171',
      iconColor: '#3B82F6',
      backgroundColor: '#4c1d1d',
    };
  }
  // default: available
  return {
    border: '#4B5563',
    borderHover: '#6B7280',
    color: '#8a90a0',
    iconColor: '#3B82F6',
    backgroundColor: '#F5F7FA',
  };
};

const CosignLeftSection = styled.div<{ $lgTone?: boolean }>(({ $lgTone }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  backgroundColor: '#2563EB',
  padding: $lgTone ? '4px 10px' : '6px 12px',
  borderRadius: $lgTone ? '6px 0 0 6px' : '5px 0 0 5px',
  color: '#fff',
  fontSize: $lgTone ? '12px' : '14px',
  fontWeight: 500,
  '.cosign-icon': {
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      // filter: 'drop-shadow(0 0 2px rgba(255, 255, 255, 0.5))',
      marginBottom: '-1.5px',
    },
  },
}))

const CosignRightSection = styled.div<CosignButtonProps & { $lgTone?: boolean }>(({ status, $lgTone }) => {
  const styles = getCosignButtonStyles(status);
  const lgAvailable = $lgTone && (!status || status === 'available');
  return {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: lgAvailable ? 'rgba(255, 255, 255, 0.08)' : styles.backgroundColor,
    padding: $lgTone ? '4px 10px' : '2.5px 12px',
    borderRadius: $lgTone ? '0 6px 6px 0' : '0 5px 5px 0',
    color: status === 'pending' ? '#FBBF24' : lgAvailable ? '#EDEBF5' : styles.color,
    fontSize: $lgTone ? '12px' : '18px',
    fontWeight: 500,
  };
})

const CosignAvailableButton = styled.span<CosignButtonProps & { $lgTone?: boolean }>(({ status, $lgTone }) => {
  const styles = getCosignButtonStyles(status);
  const lgAvailable = $lgTone && (!status || status === 'available');
  return {
    display: 'inline-flex',
    alignItems: $lgTone ? 'stretch' : 'center',
    backgroundColor: lgAvailable ? 'transparent' : styles.backgroundColor,
    borderRadius: $lgTone ? '6px' : '5px',
    overflow: 'hidden',
  };
})

const InviteApplicantButton = styled.button({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  color: '#9CA3AF',
  fontSize: '14px',
  fontWeight: 500,
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  transition: 'color 0.2s ease',
  '&:hover': {
    color: '#D1D5DB',
  },
  '.external-icon': {
    display: 'flex',
    alignItems: 'center',
  },
})


const ScreenedMeta = styled.div({
  font: "300 12.5px/1.5 'Work Sans', sans-serif",
  color: '#A9A3C9',
  marginTop: '4px',
})

const LumenV2Header = ({ lumenRun, result = undefined, card = false, minimal = false, eligibleForCosign = false, cosignApplication = null, lgTone = false }) => {
  const useDealsListUiRedesign = useVeroFlag(VeroFlag.useDealsListUiRedesign)
  const showScore = _.isNil(result)
  const status = result?.evaluation ? (!!result?.result ? 'passed' : 'failed') : 'no-result'
  const lastEntry = _.last(result?.evaluation?.history) as any
  const checks = _.filter(result?.evaluation?.history, entry => _.isBoolean(entry?.result))

  // Get triggered outcomes and outcomes for accurate check status
  const triggeredOutcomes = result?.evaluation?.triggeredOutcomes || []
  const outcomes = lumenRun?.outcomes || []
  const checkStatuses = checks.map(entry => getCheckStatusFromOutcomes(entry, triggeredOutcomes, outcomes))
  const passedCount = checkStatuses.filter(s => s === 'pass').length
  const failedCount = checkStatuses.filter(s => s === 'fail' || s === 'warn').length

  // Move useMemo outside conditional to avoid hooks order violation
  const cosignStatusMessage = useMemo(() => {
    if (!cosignApplication) {
      return 'Cosign Available'
    }
    if (cosignApplication?.status === 'pending' && cosignApplication?.displayStatusReadable) {
      return cosignApplication?.displayStatusReadable
    }
    return `Cosign ${cosignApplication?.status}`
  }, [cosignApplication])

  if (showScore) {
    if (lumenRun?.error) {
      return null
    }

    const color = getColorForLumenRun(lumenRun)

    if (minimal) {
      return (
        <>
          <div className={buildClassName(['vega title small lumen-score-label', color?.className && color?.className])}>{displayName(lumenRun?.scoreLabel)}</div>
          <Timestamp timestamp={lumenRun?.timestamp} small />
        </>
      )
    }

    return (
      <header className='vega flex no-gap' style={lgTone ? { padding: 0 } : undefined}>
        {!useDealsListUiRedesign && !lgTone && <LumenIcon className='vega icon large lumen' />}
        <div className={buildClassName(['vega title x-large lumen-score-label', color?.className && color?.className])} style={color?.style && color?.style}>
          <div
            className='vega sub-title'
            style={
              lgTone
                ? { font: "600 10.5px 'Work Sans', sans-serif", letterSpacing: '.14em', textTransform: 'uppercase', color: '#A9A3C9' }
                : useDealsListUiRedesign
                ? { paddingLeft: '2px' }
                : {}
            }
          >
            {lgTone ? 'Recommendation' : useDealsListUiRedesign ? 'VERO Recommendation' : 'lumen recommends'}
          </div>
          <div style={lgTone ? { font: "600 26px 'Work Sans', sans-serif", letterSpacing: '.03em', lineHeight: 1.1, marginTop: '3px', textTransform: 'uppercase' } : undefined}>
            {displayName(lumenRun?.scoreLabel)}
          </div>
          {lgTone && lumenRun?.timestamp && <ScreenedMeta>Screened {bannerDate(lumenRun.timestamp)}.</ScreenedMeta>}
          {(eligibleForCosign || cosignApplication) && <CosignActionsContainer>
          <CosignAvailableButton
            $lgTone={lgTone}
            status={
              cosignApplication?.status || 'available'
            }
          >
            <CosignLeftSection $lgTone={lgTone}>
              <span className="cosign-icon">
               <Icon.CosignPersonIcon width={lgTone ? 66 : 80} height={lgTone ? 15 : 18} iconFill="#fff" textFill="#fff" />
              </span>
            </CosignLeftSection>
            <CosignRightSection $lgTone={lgTone} status={cosignApplication?.status || 'available'}>
              {cosignStatusMessage.replace('Cosign', '').trim()}
            </CosignRightSection>
          </CosignAvailableButton>
          {/* {!cosignApplication && <InviteApplicantButton onClick={() => console.log('Invite applicant clicked')}>
            Invite applicant 
            <span className="external-icon">
              <Icon.ExternalLinkIcon width={14} height={14} />
            </span>
          </InviteApplicantButton>} */}
        </CosignActionsContainer>}
        </div>
        
        

        {/*<span className="score">{lumenRun?.score}</span>*/}
      </header>
    )
  }

  const body = <>
    {!result?.evaluation && <span className='vega info'>No formula or not enough data to evaluate.</span>}
    {lastEntry && (card ? <ExplanationTooltip entry={lastEntry} titleOnly /> : <span className='vega info'>{lastEntry?.explanation?.output?.title}</span>)}
  </>

  return card ? <ServiceCard
    status={status}
    label='lumen'
    value={_.lowerCase(status)}
    timestamp={lumenRun?.timestamp}
  >
    {result?.evaluation && <div className='vega flex'>
      <div className='vega flex centered no-gap'>
        <Icon.CheckGreenSmall className='vega icon' />
        <span className='vega info'>{passedCount}</span>
      </div>

      <div className='vega flex centered no-gap'>
        <Icon.CrossRedSmall className='vega icon' />
        <span className='vega info'>{failedCount}</span>
      </div>
    </div>}

    {body}
  </ServiceCard> : <ServiceHeader
    title={_.lowerCase(status)}
    status={status}
    timestamp={lumenRun?.timestamp}
  >
    {body}
  </ServiceHeader>
}

export default LumenV2Header
